import {
  Breadcrumbs,
  Box,
  Button,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/joy';
import { AxiosError } from 'axios';
import { CreateEditSellForm } from '@/components/Pages/Sells/Create/CreateEditSellForm';
import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { PrintRemit } from '@/components/Pages/Sells/PrintRemit';
import { QueryResponse, OrderResponseDTO } from '@/types';
import { SellEditProvider, useSellEdit } from '@/contexts/SellEditContext';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/axios';
import { PaymentModal } from '@/components/Pages/Sells/PaymentModal';

export const Route = createFileRoute('/_protected/sells/$id')({
  component: SellDetail,
});

function SellDetail() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const { isSuperAdmin, isAdmin } = useAuth();

  const { data: order, isLoading } = useQuery<
    QueryResponse<OrderResponseDTO>,
    AxiosError,
    OrderResponseDTO
  >({
    queryKey: ['orders', id],
    queryFn: () => api.get(`/order/${id}`),
    enabled: !!id,
    select: ({ data }) => data.data,
  });

  const { mutate: acceptOrder, isPending: isAcceptingOrder } = useMutation({
    mutationFn: () => api.put(`/order/${id}/accept`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });

      toast.success('Venta aceptada correctamente');
    },
    onError: () => {
      toast.error('Error al aceptar la venta');
    },
  });

  const { mutate: confirmOrder, isPending: isConfirmingOrder } = useMutation({
    mutationFn: () => api.put(`/order/${id}/confirm`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['stock'] });
      queryClient.invalidateQueries({ queryKey: ['articles'] });

      toast.success('Venta confirmada correctamente');
    },
    onError: (error: AxiosError<{ errors: { Order: string } }>) => {
      toast.error(error.response?.data.errors.Order);
    },
  });

  const { mutate: cancelOrder, isPending: isCancellingOrder } = useMutation({
    mutationFn: () => api.put(`/order/${id}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['stock'] });
      queryClient.invalidateQueries({ queryKey: ['articles'] });

      toast.success('Venta cancelada correctamente');
    },
    onError: () => {
      toast.error('Error al cancelar la venta');
    },
  });

  const { mutate: returnOrder, isPending: isReturningOrder } = useMutation({
    mutationFn: () => api.put(`/order/${id}/return`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['stock'] });
      queryClient.invalidateQueries({ queryKey: ['articles'] });

      toast.success('Venta devuelta correctamente');
    },
    onError: () => {
      toast.error('Error al devolver la venta');
    },
  });

  if (isLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (!order) {
    return <div>venta no encontrada</div>;
  }

  // Inner component to use context
  const SellDetailWithEditContext = ({
    order,
  }: {
    order: OrderResponseDTO;
  }) => {
    const { isEdit, setIsEdit } = useSellEdit();
    return (
      <>
        <Stack
          direction='row'
          justifyContent='space-between'
          mb={2}
          flexWrap='wrap'
        >
          <Breadcrumbs size='sm'>
            <Link to='/sells'>Ventas</Link>
            <Typography>{id}</Typography>
          </Breadcrumbs>
          <Stack direction='row' gap={2} flexWrap='wrap'>
            {(isSuperAdmin || isAdmin) && (
              <Button
                variant='outlined'
                color='neutral'
                onClick={() => setIsEdit(!isEdit)}
                disabled={
                  order.status !== 'open' && order.status !== 'in_progress'
                }
              >
                {isEdit ? 'Cancelar' : 'Editar'}
              </Button>
            )}
            {(isSuperAdmin || isAdmin) &&
              (order.status === 'confirmed' || order.status === 'paid') && (
                <Button
                  variant='outlined'
                  color='success'
                  onClick={() => returnOrder()}
                  loading={isReturningOrder}
                  disabled={isEdit}
                >
                  Devolver
                </Button>
              )}
            {(isSuperAdmin || isAdmin) && order.status === 'open' && (
              <Button
                variant='outlined'
                color='success'
                onClick={() => acceptOrder()}
                loading={isAcceptingOrder}
                disabled={isEdit}
              >
                Aceptar
              </Button>
            )}
            {(isSuperAdmin || isAdmin) && order.status === 'in_progress' && (
              <Button
                variant='outlined'
                color='success'
                onClick={() => confirmOrder()}
                loading={isConfirmingOrder}
                disabled={isEdit}
              >
                Confirmar
              </Button>
            )}
            {(isSuperAdmin || isAdmin) && order.status === 'confirmed' && (
              <PaymentModal
                orderId={order.id}
                isEdit={isEdit}
                total={order.total}
              />
            )}
            {(isSuperAdmin || isAdmin) &&
              order.status !== 'paid' &&
              order.status !== 'cancelled' && (
                <Button
                  variant='outlined'
                  color='danger'
                  onClick={() => cancelOrder()}
                  loading={isCancellingOrder}
                  disabled={isEdit}
                >
                  Cancelar
                </Button>
              )}

            <PrintRemit order={order} isEdit={isEdit} />
          </Stack>
        </Stack>

        <CreateEditSellForm order={order} />
      </>
    );
  };

  return (
    <SellEditProvider>
      <SellDetailWithEditContext order={order} />
    </SellEditProvider>
  );
}
